import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Box, Shield, Truck, Sparkles, ChevronDown, Send, Instagram } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import SEO from '../components/SEO';
import { useLang } from '../context/LangContext';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

import { normalizeCmsImages, readSessionCache } from '../api/config';
import { resolveMediaUrl } from '../utils/media';
import { normalizeProducts } from '../utils/products';
import { cmsApi, productsApi, settingsApi } from '../api/requests';

const FEATURED_CACHE_KEY = 'featured-products-cache';

export default function Home() {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [settings, setSettings] = useState({ telegramUrl: '', instagramUrl: '' });
  const [cms, setCms] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t, lang } = useLang();

  useEffect(() => {
    const fetchData = async () => {
      const cachedProducts = readSessionCache(FEATURED_CACHE_KEY);
      if (cachedProducts) {
        setFeaturedProducts(normalizeProducts(cachedProducts).slice(0, 4));
      }

      try {
        const [productsRes, settingsRes, cmsRes] = await Promise.all([
          productsApi.getFeatured(),
          settingsApi.getPublic(),
          cmsApi.getAll()
        ]);
        const productsData = normalizeProducts(productsRes.data);

        setFeaturedProducts(productsData.slice(0, 4));
        setSettings(settingsRes.data);
        setCms(normalizeCmsImages(cmsRes.data));
        sessionStorage.setItem(FEATURED_CACHE_KEY, JSON.stringify(productsRes.data));
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const hero = cms?.home?.hero || {};
  const faq = cms?.faq || {};

  return (
    <div data-testid="home-page" className="min-h-screen bg-[#0A0A0A]">
      <SEO page="home" lang={lang} path="/" />

      {/* Hero */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 via-transparent to-purple-500/5" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="order-2 lg:order-1 text-center lg:text-left stagger-children">
              <span className="inline-block text-xs tracking-[0.2em] uppercase font-bold text-indigo-400 mb-4">{t('hero.badge')}</span>
              <h1 className="text-5xl lg:text-6xl xl:text-7xl font-['Outfit'] font-light tracking-tighter text-white mb-6">
                {t('hero.title')}
                <span className="block gradient-text font-medium">{t('hero.titleAccent')}</span>
              </h1>
              <p className="text-lg text-zinc-400 max-w-xl mx-auto lg:mx-0 mb-8 leading-relaxed">{t('hero.subtitle')}</p>
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
                <Link to="/catalog" data-testid="hero-browse-btn"
                  className="group inline-flex items-center space-x-2 px-8 py-4 bg-indigo-500 hover:bg-indigo-600 text-white font-medium rounded-full transition-all hover:scale-105 glow-hover">
                  <span>{t('hero.primaryBtn')}</span><ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link to="/custom-order" data-testid="hero-custom-btn"
                  className="inline-flex items-center space-x-2 px-6 py-4 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium rounded-full transition-colors">
                  <Sparkles className="w-4 h-4" /><span>{t('hero.secondaryBtn')}</span>
                </Link>
              </div>
            </div>
            <div className="order-1 lg:order-2 relative">
              <div className="relative aspect-square max-w-lg mx-auto">
                <div className="absolute inset-0 bg-indigo-500/20 blur-3xl rounded-full" />
                {hero.heroImage && <img src={resolveMediaUrl(hero.heroImage)} alt="Premium Collectible Figurine" className="relative z-10 w-full h-full object-contain animate-float" />}
              </div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce"><ChevronDown className="w-6 h-6 text-zinc-500" /></div>
      </section>

      {/* Featured */}
      <section data-testid="featured-section" className="py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-indigo-400 mb-4 block">{t('featured.badge')}</span>
            <h2 className="text-3xl lg:text-4xl font-['Outfit'] font-light tracking-tight text-white">{t('featured.title')}</h2>
          </div>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => <div key={i} className="bg-zinc-900 rounded-2xl aspect-square animate-pulse" />)}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children">
            {(Array.isArray(featuredProducts) ? featuredProducts : []).map((product) => (
  <ProductCard key={product.id} product={product} />
))}
            </div>
          )}
          <div className="text-center mt-12">
            <Link to="/catalog" data-testid="view-all-btn" className="inline-flex items-center space-x-2 text-indigo-400 hover:text-indigo-300 font-medium transition-colors">
              <span>{t('featured.viewAll')}</span><ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* About */}
      <section id="about" data-testid="about-section" className="py-24 bg-[#121212]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-indigo-400 mb-4 block">{t('about.badge')}</span>
              <h2 className="text-3xl lg:text-4xl font-['Outfit'] font-light tracking-tight text-white mb-6">
                {t('about.title')}<span className="block text-indigo-400">{t('about.titleAccent')}</span>
              </h2>
              <div className="space-y-4 text-zinc-400 leading-relaxed">
                <p>{t('about.p1')}</p><p>{t('about.p2')}</p><p>{t('about.p3')}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Box, key: 'premiumQuality' },
                { icon: Shield, key: 'authenticCollectibles' },
                { icon: Truck, key: 'worldwideShipping' },
                { icon: Sparkles, key: 'customOrders' },
              ].map(({ icon: Icon, key }) => (
                <div key={key} className="p-6 bg-white/5 border border-white/10 rounded-2xl hover:border-indigo-500/50 transition-colors">
                  <Icon className="w-8 h-8 text-indigo-400 mb-4" />
                  <h3 className="text-white font-medium mb-2 font-['Outfit']">{t(`about.${key}`)}</h3>
                  <p className="text-zinc-500 text-sm">{t(`about.${key}Desc`)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" data-testid="how-it-works-section" className="py-24 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-indigo-400 mb-4 block">{t('howItWorks.badge')}</span>
            <h2 className="text-3xl lg:text-4xl font-['Outfit'] font-light tracking-tight text-white">{t('howItWorks.title')}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[1, 2, 3, 4].map(n => (
              <div key={n} className="relative">
                {n < 4 && <div className="hidden lg:block absolute top-8 left-full w-full h-px bg-gradient-to-r from-indigo-500/50 to-transparent" />}
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-500/10 border border-indigo-500/30 rounded-full mb-6">
                    <span className="text-2xl font-['Outfit'] font-bold text-indigo-400">{n}</span>
                  </div>
                  <h3 className="text-white font-medium mb-2 font-['Outfit']">{t(`howItWorks.step${n}`)}</h3>
                  <p className="text-zinc-500 text-sm">{t(`howItWorks.step${n}Desc`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" data-testid="faq-section" className="py-24 bg-[#121212]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-indigo-400 mb-4 block">{t('faq.badge')}</span>
            <h2 className="text-3xl lg:text-4xl font-['Outfit'] font-light tracking-tight text-white">{t('faq.title')}</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {(faq.items || []).map((item, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="bg-white/5 border border-white/10 rounded-xl px-6 data-[state=open]:border-indigo-500/50">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left text-white hover:text-indigo-400 font-medium py-6 [&[data-state=open]>svg]:rotate-180">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-zinc-400 pb-6">{item.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" data-testid="contact-section" className="py-24 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-indigo-400 mb-4 block">{t('contact.badge')}</span>
          <h2 className="text-3xl lg:text-4xl font-['Outfit'] font-light tracking-tight text-white mb-6">{t('contact.title')}</h2>
          <p className="text-zinc-400 max-w-2xl mx-auto mb-10">{t('contact.subtitle')}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {settings.telegramUrl && (
              <a href={settings.telegramUrl} target="_blank" rel="noopener noreferrer" data-testid="contact-telegram-btn"
                className="group inline-flex items-center space-x-3 px-8 py-4 bg-indigo-500 hover:bg-indigo-600 text-white font-medium rounded-full transition-all hover:scale-105">
                <Send className="w-5 h-5" /><span>{t('contact.telegram')}</span>
              </a>
            )}
            {settings.instagramUrl && (
              <a href={settings.instagramUrl} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram-btn"
                className="group inline-flex items-center space-x-3 px-8 py-4 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-medium rounded-full transition-colors">
                <Instagram className="w-5 h-5" /><span>{t('contact.instagram')}</span>
              </a>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
